// React
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @tanstack/react-query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Grid, Stack } from "@mui/material";

// Third-party
import moment from "moment";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Contexts
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";
import { MotivatorsSectionProvider, useSectionMotivators } from "@/Motivators/context/Common/MotivatorsSectionContext";

// Constants
import actions from "@/Constants/actionsList";

// Layouts
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import SmallActionButton from "@Layouts/components/Buttons/SmallActionButton";

// Customs
import SpiffEntrySectionLoader from "@Motivators/layouts/components/Skeletons/SpiffEntrySectionLoader";
import HeaderMotivatorContainer from "@/Motivators/layouts/components/HeaderMotivatorContainer";
import TemporaryGuaranteedTable from "@Motivators/components/TemporaryGuaranteed/Tables/TemporaryGuaranteedTable";

// Assets
import empty from "@/assets/images/commons/empty.png";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Helpers
import {
	canAddItems,
	canUserEditMotivators,
	defineTypeEffetiveDate,
	determineDate,
	handleUpdateEffectiveDateTime,
	handleUpdateTitle,
	isNewItem,
	onMutateTitle,
	removeUnsaveItemInTree,
	unMaskId,
} from "@/Motivators/helpers";

//Constants
import { ID_NEW_SETUP } from "@/Motivators/constants";
import { DATE_FORMAT } from "@/Constants/date";

function ContentRenderer(props) {
	const {
		locationId,
		motivatorType,
		isLoading,
		setIsLoading,
		subscriptions,
		setSelection,
		identifiers: { title: newTitleDefault, attribute, department },
	} = useMotivators();
	const { tableRows, commissionCycle } = useSectionMotivators();
	const { isLoadingData, selectedItem, data, createSetup, hasSpecialEditing } = props;
	const [selectedId, setSelectedId] = useState(null);

	const { mutations } = useMotivators();

	const queryClient = useQueryClient();

	// Hooks
	const { useAuth } = useAuthorization();

	const [paramsHeader, setParamsHeader] = useState({ title: newTitleDefault, from: null, to: null });

	const [loadingDateRange, setLoadingDateRange] = useState({
		from: false,
		to: false,
	});

	// Permissions
	const EDIT_TEMP_GUARANTEED_SETUP = useAuth(actions.EDIT_TEMP_GUARANTEED_SETUP);
	const MASTER_PERMISSION = canUserEditMotivators(hasSpecialEditing, commissionCycle?.status);

	/******************************************************* MUTATIONS *****************************************************/
	const { mutate: mutateTitle } = useMutation({
		mutationKey: [motivatorType + "Entry"],
		mutationFn: (title) => {
			return mutations.updateSetupTitle({
				locationId,
				department,
				listId: selectedId,
				value: title,
			});
		},
		onMutate: (title) => onMutateTitle(queryClient, [motivatorType + "Entry", selectedItem?._id, locationId], title),
		onError: (_error, _variables, context) =>
			queryClient.setQueryData([motivatorType + "Entry", selectedItem?._id, locationId], context.previousData),
	});

	const { mutate: mutateDate } = useMutation({
		mutationKey: [motivatorType + "Entry", selectedItem?._id, locationId],
		mutationFn: (input) => {
			return mutations.updateEffectiveDate({
				locationId,
				department,
				effectiveDateName: defineTypeEffetiveDate(input.type),
				listId: selectedId,
				commissionCycleId: selectedItem?.commissionCycleId,
				effectiveDateValue: input.dateValue,
			});
		},
	});

	const { mutate: mutateDeleteTemporaryGuaranteedSetup } = useMutation({
		mutationKey: ["deleteTemporaryGuaranteedSetup"],
		mutationFn: (input) => mutations.deleteSetup(input),
	});

	/******************************************************** HANDLERS *****************************************************/
	const onEditTitleTemp = (title) => {
		handleUpdateTitle({
			title,
			setParamsHeader,
			titleDefault: newTitleDefault,
			paramsAction: paramsHeader,
			selectedItemId: selectedItem?._id,
			commissionCycleId: selectedItem?.commissionCycleId,
			setLoading: setIsLoading,
			mutateFunction: mutateTitle,
			createAction: createSetup,
		});
	};

	const onEditEffectiveDateTemp = (dateValue, type) => {
		handleUpdateEffectiveDateTime({
			dateValue,
			type,
			setParamsHeader,
			titleDefault: newTitleDefault,
			paramsAction: paramsHeader,
			selectedItemId: selectedItem?._id,
			commissionCycleId: selectedItem?.commissionCycleId,
			setLoading: setLoadingDateRange,
			mutateFunction: mutateDate,
			createAction: createSetup,
		});
	};

	const removeTemporaryGuaranteedSetup = () => {
		const unitListId = unMaskId(selectedItem?._id);
		if (unitListId) {
			if (unitListId === ID_NEW_SETUP) {
				const dataT = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);
				const newData = removeUnsaveItemInTree(selectedItem.commissionCycleId, dataT, attribute, motivatorType);
				queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], newData);
				setSelection(null);
			} else {
				try {
					setIsLoading(true);
					mutateDeleteTemporaryGuaranteedSetup({
						locationId,
						unitListId,
						commissionCycleId: selectedItem.commissionCycleId,
					});
				} catch (error) {
					console.log("Error removing List", error);
				}
			}
		}
	};

	const handleDeleteItems = (itemsSetup, effectiveDate) => {
		const effectiveDateName = effectiveDate.type === "from" ? "effectiveDateFrom" : "effectiveDateTo";
		const effectiveDateValue = effectiveDate.dataTime;
		setIsLoading(true);

		mutations.updateSetup({
			locationId,
			effectiveDateName,
			department,
			effectiveDateValue: moment(moment.utc(effectiveDateValue).format(DATE_FORMAT.monthAbbrDatePadded)).startOf("day"),
			items: itemsSetup,
			listId: selectedId,
		});
	};

	useEffect(() => {
		setParamsHeader({
			from: determineDate(selectedItem?.effectiveDateFrom),
			to: determineDate(selectedItem?.effectiveDateTo),
			title: selectedItem?.title,
		});
		setSelectedId(unMaskId(selectedItem?._id));
	}, [selectedItem]);

	if (isLoadingData) {
		return <SpiffEntrySectionLoader />;
	}

	if (!selectedItem) {
		return <EmptyBody image={String(empty)} title="No Item selected" bodyHeight="510px" />;
	}

	const formatDate = (dateValue) => moment.utc(dateValue).format(DATE_FORMAT.monthAbbrDatePadded);

	const isButtonDisabled = () => {
		const effDateFrom = moment(formatDate(selectedItem?.effectiveDateFrom)).startOf("day");
		const today = moment().utc(false);
		const noTableRows = tableRows.length === 0; // Asuming that the tableRows are an array

		const isComponentDisabled =
			(Boolean(isLoadingData) || Boolean(selectedItem?.parentCommissionCycleId) || effDateFrom.isBefore(today)) && !noTableRows;
		return !EDIT_TEMP_GUARANTEED_SETUP || isComponentDisabled;
	};

	return (
		<Grid container spacing={5} component={"section"}>
			<Grid item xs={12} component={"header"}>
				<Stack direction="row" justifyContent={"space-between"}>
					<HeaderMotivatorContainer
						hasEditPermission={EDIT_TEMP_GUARANTEED_SETUP}
						dateRange={{ from: paramsHeader.from, to: paramsHeader.to }}
						setLoadingDateRange={setLoadingDateRange}
						loadingDateRange={loadingDateRange}
						isLoadingData={isLoadingData}
						title={paramsHeader.title}
						defaultTitle={newTitleDefault}
						onEditTitle={onEditTitleTemp}
						onEditEffectiveDate={onEditEffectiveDateTemp}
						onDeleteItems={handleDeleteItems}
						toDisabled={!data?.effectiveDateFrom}
						toMinDateTime={
							data?.effectiveDateFrom
								? moment(data?.effectiveDateFrom).utc().add(1, "day")
								: moment(selectedItem?.commissionCycleEndDate).utc()
						}
						masterPermission={MASTER_PERMISSION}
					/>
				</Stack>
			</Grid>
			<Grid item xs={12} height="430px" className="!pt-[4px]">
				<TemporaryGuaranteedTable
					data={data?.items || []}
					currentMonth={true}
					locationId={locationId}
					commissionCycleId={selectedItem.commissionCycleId}
					subscriptions={subscriptions || null}
					loading={isLoadingData}
					selectedItem={selectedItem}
					canAddItems={canAddItems(
						EDIT_TEMP_GUARANTEED_SETUP || MASTER_PERMISSION,
						{ from: paramsHeader.from, to: paramsHeader.to },
						selectedId
					)}
					masterPermission={MASTER_PERMISSION}
				/>
			</Grid>
			<Grid xs={12} item alignSelf={"flex-end"} className="absolute right-[50px] bottom-[34px]">
				<SmallActionButton
					disabled={isButtonDisabled()}
					buttonText={"Delete List"}
					buttonTextConfirm={"Confirm Delete"}
					description={""}
					onClick={removeTemporaryGuaranteedSetup}
					loading={isLoading}
				/>
			</Grid>
		</Grid>
	);
}

export default function TemporaryGuaranteedEntrySection(props) {
	const { queries, motivatorType, locationId, setIsLoading, identifiers } = useMotivators();

	const QUERY_IDENTIFIERS = [motivatorType + "Entry", "recipientEntry"];
	const { clearCache } = useCacheCleaner();
	/************************************************ QUERIES *************************************************/
	const { data, isLoading: isLoadingData } = useQuery({
		queryKey: [motivatorType + "Entry", props.selectedItem?._id, locationId],
		queryFn: async () => {
			const listId = unMaskId(props.selectedItem?._id);

			setIsLoading(true);
			const result = queries.getBodyData({ locationId, listId, department: identifiers.department });
			setIsLoading(false);
			return result;
		},
		enabled: Boolean(locationId) && Boolean(props.selectedItem?._id) && !isNewItem(props.selectedItem?._id),
		staleTime: Infinity,
	});

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	return (
		<RoundedBox sx={{ minHeight: "630px" }} component="section">
			<MotivatorsSectionProvider>
				<ContentRenderer {...props} data={data} isLoadingData={isLoadingData} />
			</MotivatorsSectionProvider>
		</RoundedBox>
	);
}

TemporaryGuaranteedEntrySection.propTypes = {
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		title: PropTypes.string,
		startDate: PropTypes.string,
		endDate: PropTypes.string,
		commissionCycleId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
	}),
	hasSpecialEditing: PropTypes.bool,
};

ContentRenderer.propTypes = {
	isLoadingData: PropTypes.bool,
	commissionCycleId: PropTypes.string,
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		title: PropTypes.string,
		startDate: PropTypes.object,
		endDate: PropTypes.object,
		commissionCycleId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
		parentCommissionCycleId: PropTypes.string,
	}),
	createSetup: PropTypes.func,
	data: PropTypes.shape({
		title: PropTypes.string,
		items: PropTypes.array,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
	}),
	hasSpecialEditing: PropTypes.bool,
};
